import { useMemo, useState } from 'react';

import {
  useLegalClientCreateLegalHeadFirstHead,
  useLegalClientLegalHeadFirstHeadSavePersonalData,
} from 'api/requests/onboarding/legal/legalHeadFirstHead';
import { ErrorCode } from 'api/types/error';
import { useNotify } from 'libs/notify';
import {
  PersonalDataFormType,
  FormErrors,
} from 'modules/onboarding/common/forms/personalDataForm';

import { useOnboardingContext } from './useOnboardingContext';

export const usePersonalData = () => {
  const { goToBack, data, goToNext } = useOnboardingContext();
  const notify = useNotify();

  const [submitLoading, setSubmitLoading] = useState(false);

  const {
    savePersonalData,
    loading: submitPersonalDataLoading,
    error: errorPersonalData,
  } = useLegalClientLegalHeadFirstHeadSavePersonalData();

  const { createFirstHead, error: createError } =
    useLegalClientCreateLegalHeadFirstHead();

  const initialValues = useMemo<PersonalDataFormType | undefined>(
    () =>
      data.firstHead
        ? {
            email: data.firstHead.email,
            firstName: data.firstHead.firstName,
            lastName: data.firstHead.lastName,
            dateOfBirth: data.firstHead.dateOfBirth,
            middleName: data.firstHead.middleName,
          }
        : undefined,
    [data.firstHead]
  );

  const onSubmit = async (values: PersonalDataFormType) => {
    if (values) {
      if (!data.firstHead) {
        setSubmitLoading(true);

        try {
          const res = await createFirstHead(
            data.legalClient.id!,
            data.legalHeadId,
            values
          );
          if (res) {
            data.onCreateFirstHead(res.id);
            goToNext();
          }
        } catch (error) {
          setSubmitLoading(false);
        }
      } else {
        const res = await savePersonalData(
          data.legalClient.id,
          data.legalHeadId,
          data.firstHead.id,
          values
        );

        if (res) {
          goToNext();
        }
      }
    }
  };

  const formError = useMemo((): FormErrors | undefined => {
    if (createError?.code === ErrorCode.USER_WITH_EMAIL_ALREADY_EXISTS) {
      return { email: createError?.message };
    }

    if (errorPersonalData?.code === ErrorCode.USER_WITH_EMAIL_ALREADY_EXISTS) {
      return { email: errorPersonalData?.message };
    }

    if (createError || errorPersonalData) {
      notify.error(createError ?? errorPersonalData);
    }

    return undefined;
  }, [createError, errorPersonalData, notify]);

  return {
    onSubmit,
    goToBack,
    initialValues,
    formError,
    submitLoading: submitLoading || submitPersonalDataLoading,
  };
};
